import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Langs from "../../../app/lang/langs";
import { showErrorToast, checkPermission } from "../../../app/utils/helpers";
import { config } from "../../../app/utils/config";
import { prefillPlanning, getPlanning } from "../planningSlice";
import { TransparentDatePicker } from "../../../components/buttons/TransparentDatePicker";
import moment from "moment";

export function MobileDuplicateModal({ show, onHide }) {
  const dispatch = useDispatch();

  const [durationType, setDurationType] = useState("WEEKLY");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTargetDate, setSelectedTargetDate] = useState(new Date());

  const selectedTeamsIds = useSelector((state) => state.teams.selectedTeamsIds);
  const collabs = useSelector((state) => state.collabs.list);

  function isManager(teamId) {
    return checkPermission(config.scopes.shifts, config.permissionTypes.edit, teamId);
  }

  function handleDuplicatePlanning() {
    let range = "month";
    if (durationType == "WEEKLY") {
      range = "week";
    }
    let sourceFrom = moment(selectedDate).startOf(range);
    let sourceTo = moment(selectedDate).endOf(range);

    let targetFrom = moment(selectedTargetDate).startOf(range);
    let targetTo = moment(selectedTargetDate).endOf(range);

    if (sourceFrom && sourceTo && targetFrom && targetTo) {
      let params = {
        teamIds: selectedTeamsIds.filter((id) => isManager(id)),
        sourceFrom: sourceFrom.format("x"),
        sourceTo: sourceTo.format("x"),
        targetFrom: targetFrom.format("x"),
        targetTo: targetTo.format("x"),
      };
      if (durationType == "WEEKLY") {
        params.targetWeek = targetTo.week();
      } else {
        params.targetMonth = targetTo.month();
      }
      dispatch(prefillPlanning(params)).then((response) => {
        if (response["payload"]["responseData"]["status"] == 1) {
          loadPlanning();
        }
        if (response["payload"]["responseData"]["status"] == 4) {
          showErrorToast(response["payload"]["responseData"]["message"]);
        }
        if (response["payload"]["responseError"]) {
          showErrorToast();
        }
      });
    }
    onHide();
  }

  function loadPlanning() {
    if (selectedTeamsIds && selectedTeamsIds.length && collabs.length) {
      let selectedCollabsIds = collabs.reduce((array, current) => {
        array.push(current.id);
        return array;
      }, []);
      dispatch(getPlanning({ selectedTeamsIds, selectedCollabsIds }));
    }
  }

  return (
    <Dialog
      open={show}
      onClose={onHide}
      fullWidth
      maxWidth="xs"
      sx={{ zIndex: 1400 }}
      PaperProps={{
        sx: {
          borderRadius: "16px",
          mx: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          pb: 1,
        }}
      >
        <ContentCopyIcon sx={{ color: "#FF9015", fontSize: 28 }} />
        <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>
          <Langs str="Duplicate planning" />
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        {/* Duration toggle */}
        <Box>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: "text.secondary" }}>
            <Langs str="Duration" />
          </Typography>
          <ToggleButtonGroup
            value={durationType}
            exclusive
            onChange={(e, val) => { if (val) setDurationType(val); }}
            fullWidth
            sx={{
              "& .MuiToggleButton-root": {
                borderRadius: "999px",
                textTransform: "none",
                fontWeight: 600,
                minHeight: 44,
                fontSize: "0.875rem",
                "&.Mui-selected": {
                  backgroundColor: "#FF9015",
                  color: "#fff",
                  "&:hover": { backgroundColor: "#e6820d" },
                },
              },
            }}
          >
            <ToggleButton value="WEEKLY">
              <Langs str="Week" />
            </ToggleButton>
            <ToggleButton value="MONTHLY">
              <Langs str="Month" />
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Source date */}
        <Box>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: "text.secondary" }}>
            <Langs str="Source" />
          </Typography>
          <TransparentDatePicker
            viewType={durationType}
            onChange={(date) => setSelectedDate(date)}
            value={selectedDate}
          />
        </Box>

        {/* Target date */}
        <Box>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: "text.secondary" }}>
            <Langs str="Target" />
          </Typography>
          <TransparentDatePicker
            viewType={durationType}
            onChange={(date) => setSelectedTargetDate(date)}
            value={selectedTargetDate}
          />
        </Box>

        {/* Warning note */}
        <Alert severity="warning" sx={{ borderRadius: "12px" }}>
          <Langs str="DUPLICATE_NOTE" />
        </Alert>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          variant="outlined"
          onClick={onHide}
          sx={{
            flex: 1,
            borderRadius: "999px",
            minHeight: 48,
            textTransform: "none",
            fontWeight: 600,
            color: "text.secondary",
            borderColor: "divider",
          }}
        >
          <Langs str="CANCEL" />
        </Button>
        <Button
          variant="contained"
          onClick={handleDuplicatePlanning}
          sx={{
            flex: 1,
            borderRadius: "999px",
            minHeight: 48,
            textTransform: "none",
            fontWeight: 600,
            backgroundColor: "#FF9015",
            "&:hover": { backgroundColor: "#e6820d" },
            "&:active": { transform: "scale(0.97)" },
          }}
        >
          <Langs str="Duplicate" />
        </Button>
      </DialogActions>
    </Dialog>
  );
}
